import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { History, Download, Search, X, FileText, FileSpreadsheet, Users, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import HistoricoGeralModal from '../components/historico/HistoricoGeralModal';
import jsPDF from 'jspdf';
import Papa from 'papaparse';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Historico = () => {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === 'dono' || user?.role === 'administrador';
  const [showGeral, setShowGeral] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('recent');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  useEffect(() => {
    fetchHistory();
    if (isAdmin) fetchUsers();
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/community/members`, { headers: getAuthHeaders() });
      setAllUsers(res.data || []);
    } catch (err) { console.error('Failed to fetch users:', err); }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const url = selectedUserId ? `${API}/historico?target_user_id=${selectedUserId}` : `${API}/historico`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setHistory(res.data || []);
    } catch (err) { console.error('Failed to fetch history:', err); toast.error('Erro ao carregar histórico'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { const p = d.split('T')[0].split('-'); return `${p[2]}/${p[1]}/${p[0]}`; }
    catch { return d; }
  };

  const getMonth = (d) => { try { return parseInt(d.split('T')[0].split('-')[1]); } catch { return 0; } };
  const getYear = (d) => { try { return parseInt(d.split('T')[0].split('-')[0]); } catch { return 0; } };

  const norm = (s) => s?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';

  const uniqueProjects = [...new Set(history.map(h => h.project_title).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(history.map(h => h.status).filter(Boolean))];

  // Get unique years from data
  const uniqueYears = [...new Set(history.map(h => getYear(h.delivery_deadline || h.created_at)).filter(y => y > 0))].sort((a, b) => b - a);
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const filtered = history.filter(h => {
    if (statusFilter && h.status !== statusFilter) return false;
    if (projectFilter && h.project_title !== projectFilter) return false;
    if (paymentFilter && h.payment_status !== paymentFilter) return false;
    if (search) {
      const q = norm(search);
      if (!norm(h.character_name).includes(q) && !norm(h.project_title).includes(q) && !norm(h.client).includes(q)) return false;
    }
    // Date filter
    if (filterMonth || filterYear) {
      const dateRef = h.delivery_deadline || h.created_at;
      if (filterMonth && getMonth(dateRef) !== parseInt(filterMonth)) return false;
      if (filterYear && getYear(dateRef) !== parseInt(filterYear)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortOrder === 'az') return (a.character_name || '').localeCompare(b.character_name || '', 'pt-BR');
    if (sortOrder === 'za') return (b.character_name || '').localeCompare(a.character_name || '', 'pt-BR');
    if (sortOrder === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
    if (sortOrder === 'falas') return (b.num_falas || 0) - (a.num_falas || 0);
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  const totalFalas = filtered.reduce((s, h) => s + (h.num_falas || 0), 0);
  const totalValor = filtered.reduce((s, h) => s + (h.valor || 0), 0);

  const exportCSV = () => {
    const data = filtered.map(h => ({
      Personagem: h.character_name, Projeto: h.project_title, Cliente: h.client,
      Status: h.status, Pagamento: h.payment_status === 'pago' ? 'Pago' : 'Não Pago',
      Falas: h.num_falas, 'Valor (R$)': h.valor?.toFixed(2),
      'Data Entrega': formatDate(h.delivery_deadline)
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = `historico.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    toast.success('CSV exportado!');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const name = selectedUserId ? (allUsers.find(u => u.id === selectedUserId)?.artist_name || 'Dublador') : (user?.artist_name || user?.name || 'Dublador');
    doc.setFontSize(18); doc.text(`Histórico - ${name}`, 20, 20);
    doc.setFontSize(10); doc.text(`Total: ${filtered.length} personagens | ${totalFalas} falas | R$ ${totalValor.toFixed(2)}`, 20, 30);
    let y = 42;
    doc.setFontSize(7);
    doc.text('PERSONAGEM', 15, y); doc.text('PROJETO', 60, y); doc.text('STATUS', 110, y); doc.text('PGTO', 135, y); doc.text('FALAS', 155, y); doc.text('VALOR', 170, y); doc.text('ENTREGA', 190, y);
    y += 6;
    filtered.forEach(h => {
      if (y > 280) { doc.addPage(); y = 20; }
      doc.text((h.character_name || '').substring(0, 22), 15, y);
      doc.text((h.project_title || '').substring(0, 25), 60, y);
      doc.text(h.status || '', 110, y);
      doc.text(h.payment_status === 'pago' ? 'Pago' : 'Não Pago', 135, y);
      doc.text(String(h.num_falas || 0), 155, y);
      doc.text(`R$ ${(h.valor || 0).toFixed(2)}`, 170, y);
      doc.text(formatDate(h.delivery_deadline), 190, y);
      y += 5;
    });
    doc.save(`historico_${name}.pdf`);
    toast.success('PDF exportado!');
  };

  const exportExcel = () => {
    const data = filtered.map(h => ({
      Personagem: h.character_name, Projeto: h.project_title, Cliente: h.client,
      Status: h.status, Pagamento: h.payment_status === 'pago' ? 'Pago' : 'Não Pago',
      Falas: h.num_falas, 'Valor (R$)': h.valor?.toFixed(2),
      'Data Entrega': formatDate(h.delivery_deadline)
    }));
    import('xlsx').then(XLSX => {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
      XLSX.writeFile(wb, `historico.xlsx`);
      toast.success('Excel exportado!');
    });
  };

  const getStatusColor = (s) => {
    if (s === 'ENTREGUE') return 'bg-green-500/20 text-green-400';
    if (s === 'ATRASADO') return 'bg-red-500/20 text-red-400';
    return 'bg-orange-500/20 text-orange-400';
  };

  const getPaymentColor = (s) => s === 'pago' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400';

  // Admin user search filter
  const filteredUsersList = allUsers.filter(u => {
    if (!userSearch) return true;
    const q = norm(userSearch);
    return norm(u.artist_name).includes(q) || norm(u.name).includes(q);
  });

  if (loading && history.length === 0) return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="historico-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <History className="h-8 w-8 text-teal-400" /> Histórico
            </h1>
            <p className="text-slate-400 mt-1">
              {selectedUserId && isAdmin ? `Visualizando: ${allUsers.find(u => u.id === selectedUserId)?.artist_name || allUsers.find(u => u.id === selectedUserId)?.name || ''}` : 'Seus personagens e trabalhos'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <button
                onClick={() => setShowGeral(true)}
                className="flex items-center gap-1.5 bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/40 text-teal-300 px-3 py-2 rounded-lg text-sm font-medium"
                data-testid="historico-geral-btn"
              >
                <TrendingUp className="h-4 w-4" /> Histórico Geral
              </button>
            )}
            <button onClick={exportCSV} className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg text-sm" data-testid="export-csv"><FileText className="h-4 w-4" /> CSV</button>
            <button onClick={exportExcel} className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg text-sm" data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</button>
            <button onClick={exportPDF} className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg text-sm" data-testid="export-pdf"><Download className="h-4 w-4" /> PDF</button>
          </div>
        </div>

        <HistoricoGeralModal open={showGeral} onClose={() => setShowGeral(false)} apiHeaders={getAuthHeaders()} />

        {/* Admin: User Selector */}
        {isAdmin && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Users className="h-5 w-5 text-teal-400 shrink-0" />
              <span className="text-sm text-slate-300 font-medium shrink-0">Ver histórico de:</span>
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Buscar usuário..."
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="user-search" />
              </div>
              <button onClick={() => { setSelectedUserId(''); setUserSearch(''); }}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${!selectedUserId ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                Meu Histórico
              </button>
            </div>
            {(userSearch || selectedUserId) && (
              <div className="flex flex-wrap gap-2 mt-3 max-h-24 overflow-y-auto">
                {filteredUsersList.slice(0, 20).map(u => (
                  <button key={u.id} onClick={() => { setSelectedUserId(u.id); setUserSearch(''); }}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedUserId === u.id ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                    data-testid={`user-select-${u.id}`}>
                    {u.artist_name || u.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-400">Personagens</p>
            <p className="text-2xl font-bold text-white">{filtered.length}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-400">Total de Falas</p>
            <p className="text-2xl font-bold text-teal-400">{totalFalas}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-400">Valor Total</p>
            <p className="text-2xl font-bold text-emerald-400">R$ {totalValor.toFixed(2)}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-400">Pagos</p>
            <p className="text-2xl font-bold text-green-400">{filtered.filter(h => h.payment_status === 'pago').length}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-400">Não Pagos</p>
            <p className="text-2xl font-bold text-red-400">{filtered.filter(h => h.payment_status !== 'pago').length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-8 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="historico-search" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
          </div>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-month-filter">
            <option value="">Todos Meses</option>
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-year-filter">
            <option value="">Todos Anos</option>
            {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-project-filter">
            <option value="">Todos Projetos</option>
            {uniqueProjects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-status-filter">
            <option value="">Todos Status</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-payment-filter">
            <option value="">Pagamento: Todos</option>
            <option value="pago">Pago</option>
            <option value="nao_pago">Não Pago</option>
          </select>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" data-testid="historico-sort">
            <option value="recent">Mais Recente</option>
            <option value="oldest">Mais Antigo</option>
            <option value="az">Nome A-Z</option>
            <option value="za">Nome Z-A</option>
            <option value="falas">Mais Falas</option>
          </select>
          {(search || projectFilter || statusFilter || paymentFilter || filterMonth || filterYear) && (
            <button onClick={() => { setSearch(''); setProjectFilter(''); setStatusFilter(''); setPaymentFilter(''); setFilterMonth(''); setFilterYear(''); }}
              className="flex items-center gap-1 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm"><X className="h-4 w-4" /> Limpar</button>
          )}
        </div>

        {/* Table */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="historico-table">
              <thead>
                <tr className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Personagem</th>
                  <th className="px-4 py-3 text-left">Projeto</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Pagamento</th>
                  <th className="px-4 py-3 text-center">Falas</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3 text-center">Entrega</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Nenhum registro encontrado</td></tr>
                ) : filtered.map(h => {
                  const isStaff = h.entry_type === 'staff';
                  return (
                  <tr key={h.id} className="border-t border-slate-700 hover:bg-slate-800/50 transition-colors" data-testid={`historico-row-${h.id}`}>
                    <td className="px-4 py-3 text-white font-medium">
                      {isStaff
                        ? <span className="text-purple-300">{h.role_label}</span>
                        : h.character_name}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{h.project_title}</td>
                    <td className="px-4 py-3 text-slate-400">{h.client || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {isStaff ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-300">Equipe</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(h.status)}`}>{h.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPaymentColor(h.payment_status)}`}>
                        {h.payment_status === 'pago' ? 'Pago' : 'Não Pago'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-teal-400 font-mono">
                      {isStaff
                        ? <span title="Duração do projeto">{h.duration_display || '-'}</span>
                        : (h.num_falas || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-mono">R$ {(h.valor || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center text-slate-400">{formatDate(h.delivery_deadline)}</td>
                  </tr>
                );})}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600 bg-slate-900">
                    <td colSpan={5} className="px-4 py-3 text-right text-white font-bold">TOTAL</td>
                    <td className="px-4 py-3 text-center text-teal-400 font-mono font-bold">{totalFalas}</td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-mono font-bold">R$ {totalValor.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Historico;
